import type { TableScrollAreaProps } from '@chakra-ui/react';
import { Flex } from '@chakra-ui/react';
import { Children, cloneElement, isValidElement, type ReactNode } from 'react';

import { Checkbox } from '../checkbox';
import type { DataTableRootProps } from './data-table';
import { DataTable } from './data-table';
import { DataTableSortIcon, type DataTableSortDirection } from './data-table-sort-icon';

export interface CheckboxRowProps {
  enableSelection?: boolean;
  isSelected?: boolean;
  onRowCheck?: (selectedIds: string[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRowId?: (row: any) => string;
  checkedRows?: string[];
}

export interface DataTableComposedProps extends Omit<TableScrollAreaProps, 'children'> {
  headers: string[];
  numericCols?: string[];
  centerColumns?: string[];
  columnWidths?: Record<string, string>;
  columnVisibility?: Record<string, boolean>;

  sortHeaders?: string[];
  onSort?: (header: string) => void;
  /** Active sort state keyed by display header label. DataTable renders sort icons and `data-sorted` styling automatically. */
  sortState?: Record<string, DataTableSortDirection>;

  getHeaderRightIcon?: (header: string) => ReactNode;
  extraHeaderCell?: ReactNode;

  /** Banner above the table inside the shared outline. Null-returning children are fine (empty slot stays hidden). */
  inlineHeader?: ReactNode;

  children: ReactNode;

  enableCheckboxSelection?: boolean;
  checkedRows?: string[];
  onRowCheck?: (selectedIds: string[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRowId?: (row: any) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isRowCheckable?: (row: any) => boolean;

  height?: string;
  isPending?: boolean;
  tableProps?: DataTableRootProps;
}

export const DataTableComposed = ({
  headers,
  numericCols = [],
  centerColumns,
  columnWidths,
  columnVisibility,
  sortHeaders,
  onSort,
  sortState,
  getHeaderRightIcon,
  extraHeaderCell,
  inlineHeader,
  children,
  enableCheckboxSelection = false,
  checkedRows = [],
  onRowCheck,
  getRowId,
  isRowCheckable = () => true,
  height,
  isPending,
  tableProps,
  ...containerProps
}: DataTableComposedProps) => {
  const visibleHeaders = columnVisibility
    ? headers.filter(h => columnVisibility[h] !== false)
    : headers;

  const checkboxSelectableRows = enableCheckboxSelection
    ? Children.toArray(children)
        .filter(isValidElement)
        .filter((child: React.ReactElement) => {
          const data = (child.props as { data?: unknown }).data;
          if (data === undefined) return false;
          return isRowCheckable(data);
        })
    : [];

  const totalCheckboxSelectableRows = checkboxSelectableRows.length;
  const allSelected = enableCheckboxSelection
    ? checkedRows.length === totalCheckboxSelectableRows && totalCheckboxSelectableRows > 0
    : false;

  const handleCheckboxSelectAll = () => {
    if (!onRowCheck || !getRowId) return;

    if (allSelected || (checkedRows.length > 0 && checkedRows.length < totalCheckboxSelectableRows)) {
      onRowCheck([]);
    } else {
      const allIds = checkboxSelectableRows.map(child => {
        const element = child as React.ReactElement;
        return getRowId((element.props as { data?: unknown }).data);
      });
      onRowCheck(allIds);
    }
  };

  return (
    <DataTable.ScrollArea height={height} inlineHeader={inlineHeader} {...containerProps}>
      <DataTable.Root stickyHeader {...tableProps}>
        <DataTable.Header>
          <DataTable.Row>
            {enableCheckboxSelection && (
              <DataTable.ColumnHeader width="40px" textAlign="center">
                <Checkbox
                  checked={checkedRows.length > 0 && !allSelected ? 'indeterminate' : allSelected}
                  onCheckedChange={() => handleCheckboxSelectAll()}
                />
              </DataTable.ColumnHeader>
            )}
            {visibleHeaders.map((header: string, idx: number) => {
              const isSortableCol = sortHeaders?.includes(header);
              const sortDirection = sortState?.[header];
              let textAlign = 'start';
              let justifyContent: string | undefined = undefined;

              if (numericCols.includes(header)) {
                textAlign = 'end';
                justifyContent = 'flex-end';
              } else if (centerColumns?.includes(header)) {
                textAlign = 'center';
                justifyContent = 'center';
              }

              return (
                <DataTable.ColumnHeader
                  key={`${idx}-${header}`}
                  textAlign={textAlign}
                  onClick={() => isSortableCol && onSort?.(header)}
                  width={{ base: columnWidths?.[header], md: 'auto' }}
                  style={{ minWidth: columnWidths?.[header] }}
                  data-sortable={isSortableCol || undefined}
                  data-sorted={sortDirection ? '' : undefined}
                >
                  <Flex
                    justifyContent={justifyContent}
                    alignItems="center"
                    cursor={isSortableCol ? 'pointer' : 'default'}
                    gap={1}
                  >
                    {header.toUpperCase()}
                    {getHeaderRightIcon?.(header)}
                    {isSortableCol && <DataTableSortIcon direction={sortDirection} />}
                  </Flex>
                </DataTable.ColumnHeader>
              );
            })}
            {extraHeaderCell}
          </DataTable.Row>
        </DataTable.Header>
        <DataTable.Body>
          {enableCheckboxSelection
            ? Children.map(children, child => {
                if (isValidElement(child)) {
                  const data = (child.props as { data?: unknown }).data;
                  if (data === undefined) {
                    return child;
                  }
                  return cloneElement(child, {
                    enableSelection: true,
                    isSelected: checkedRows.includes(getRowId?.(data as unknown) ?? ''),
                    onRowCheck,
                    getRowId,
                    checkedRows,
                  } as CheckboxRowProps);
                }
                return child;
              })
            : children}
        </DataTable.Body>
      </DataTable.Root>
      <DataTable.LoadingOverlay visible={isPending} />
    </DataTable.ScrollArea>
  );
};
